import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import PageHeader from '@/components/feature/PageHeader';
import ContributeCta from '@/components/feature/ContributeCta';
import { useSeo, siteOrigin } from '@/hooks/useSeo';
import { channels } from '@/mocks/channels';
import ChannelExplorer from './components/ChannelExplorer';
import ChannelDetailList from './components/ChannelDetailList';

const guideSteps = [
  {
    icon: 'ri-compass-3-line',
    title: '先看频道覆盖地图',
    desc: '用关键词或更新节奏筛选，确认哪个频道与你的关注点最接近，再进入下方详情。',
  },
  {
    icon: 'ri-book-open-line',
    title: '阅读频道详情介绍',
    desc: '每个频道都标注了选题侧重、更新节奏与内容形态，方便你判断是否值得长期关注。',
  },
  {
    icon: 'ri-links-line',
    title: '配合专题一起看',
    desc: '同一议题常跨多个频道，进入专题分类页可以顺着主题把相关报道串起来读。',
  },
];

const channelsJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      name: '频道列表 | 爆料官网',
      url: `${siteOrigin}/channels`,
      description:
        '爆料官网完整频道列表，涵盖娱乐、科技、财经、社会、消费维权、职场、汽车、房产、教育、健康十个频道的介绍与更新节奏。',
      inLanguage: 'zh-CN',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteOrigin },
        { '@type': 'ListItem', position: 2, name: '频道列表', item: `${siteOrigin}/channels` },
      ],
    },
    {
      '@type': 'ItemList',
      name: '爆料官网频道清单',
      numberOfItems: channels.length,
      itemListElement: channels.map((channel, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: channel.name,
        description: channel.summary,
        url: `${siteOrigin}/channels#channel-${channel.slug}`,
      })),
    },
  ],
};

export default function Channels() {
  useSeo({
    title: '频道列表 | 爆料官网栏目频道导航与更新节奏',
    description:
      '爆料官网频道列表页，完整介绍娱乐、科技、财经、社会、消费维权等十个频道的覆盖领域、选题侧重与更新节奏，帮助你按领域快速筛选内容。',
    keywords: '频道列表,爆料官网频道,栏目分类,内容领域,更新节奏,频道导航',
    path: '/channels',
    jsonLd: channelsJsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeader
          eyebrow="频道列表"
          title="十个频道，覆盖你关心的每个领域"
          description="每个频道都有清晰的定位、选题侧重与更新节奏。先用覆盖地图快速筛选，再进入详情了解频道的内容结构与适合的阅读方式。"
          crumbs={[{ label: '频道列表' }]}
          meta={[
            { label: '内容频道', value: '10 个' },
            { label: '累计收录', value: '8,600+ 篇' },
            { label: '每日更新频道', value: '5 个' },
          ]}
        />

        <section className="bg-background-50 pt-14 md:pt-20">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {guideSteps.map((step, index) => (
                <div key={step.title} className="flex gap-4 rounded-lg border border-background-300/60 bg-background-100/50 p-5">
                  <span className="w-10 h-10 shrink-0 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className={`${step.icon} text-lg`} aria-hidden="true" />
                  </span>
                  <div className="flex flex-col gap-1.5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      <span className="text-primary-600 mr-1.5">{String(index + 1).padStart(2, '0')}</span>
                      {step.title}
                    </h2>
                    <p className="text-xs md:text-sm leading-relaxed text-foreground-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ChannelExplorer />
        <ChannelDetailList />
        <ContributeCta
          title="你关注的领域还没有频道？把线索发给我们"
          desc="频道结构会随着读者关注度调整。如果某个领域内容持续增多，我们会考虑为它单独开设频道。"
        />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}